import {
  motion,
  useScroll,
  useTransform,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { ChevronDown, PackageCheck, Users, Palette, Trophy } from "lucide-react";
import { EASE } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1771440571270-e27b63085a48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxlc3BvcnRzJTIwYXJlbmElMjByYWNpbmclMjBzaW11bGF0b3IlMjBtb3Rpb24lMjBwbGF0Zm9ybXxlbnwwfHx8fDE3OTA5NjE5MDl8MA&ixlib=rb-4.1.0&q=80&w=1920";

const VALUE_PROPS = [
  { icon: PackageCheck, title: "Turnkey Solution", desc: "We handle everything." },
  { icon: Users, title: "Professional Operation", desc: "Staffed by real sim racers." },
  { icon: Palette, title: "Brand Customization", desc: "Make it your own." },
  { icon: Trophy, title: "Memorable Engagement", desc: "Real results. Lasting impact." },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1 -mb-1">
    <motion.span
      className="block will-change-transform"
      initial={{ y: "110%" }}
      animate={{ y: "0%" }}
      transition={{ delay, duration: 1, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onNavigate, onQuote }) => {
  const { scrollY } = useScroll();
  const bgY = useTransform(scrollY, [0, 900], [0, 220]);
  const contentFade = useTransform(scrollY, [0, 450], [1, 0]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const glowX = useSpring(mx, { stiffness: 50, damping: 20 });
  const glowY = useSpring(my, { stiffness: 50, damping: 20 });

  const onMouseMove = (e) => {
    mx.set((e.clientX / window.innerWidth - 0.5) * 40);
    my.set((e.clientY / window.innerHeight - 0.5) * 24);
  };

  return (
    <section
      id="top"
      className="relative min-h-screen flex flex-col overflow-hidden"
      onMouseMove={onMouseMove}
      data-testid="hero-section"
    >
      <motion.div className="absolute inset-0" style={{ y: bgY }}>
        <motion.img
          src={HERO_IMG}
          alt="Trade show booth with an active racing simulator and spectators under blue stage lighting"
          className="w-full h-full object-cover"
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1.02, opacity: 1 }}
          transition={{ duration: 1.6, ease: EASE }}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ink/95 via-ink/75 to-ink/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/60" />
      <div className="absolute inset-0 grid-lines opacity-60" />
      <motion.div
        className="absolute w-[520px] h-[520px] rounded-full bg-electric/15 blur-[140px] pointer-events-none"
        style={{ x: glowX, y: glowY, top: "20%", left: "55%" }}
      />

      <motion.div
        style={{ opacity: contentFade }}
        className="relative flex-1 flex items-center max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-32 pb-16"
      >
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2.5 font-mono text-[11px] sm:text-xs uppercase tracking-[0.3em] text-electric border border-electric/30 bg-electric/5 px-4 py-2"
            data-testid="hero-kicker"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-electric animate-pulse-glow" />
            Turnkey Sim Racing Rentals
          </motion.p>

          <h1 className="font-display font-black uppercase tracking-tight leading-[0.98] text-4xl sm:text-5xl lg:text-6xl text-slate-50 mt-7">
            <MaskedLine delay={0.3}>BRING THE RACE</MaskedLine>
            <MaskedLine delay={0.46}>
              TO YOUR <span className="text-electric">BOOTH.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.8, ease: EASE }}
            className="text-base sm:text-lg text-slate-300 mt-6 max-w-xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            Turnkey, professional sim-racing experiences for trade shows,
            corporate activations, and brand events.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.8, ease: EASE }}
            className="flex flex-wrap items-center gap-4 mt-9"
          >
            <button
              onClick={() => onNavigate("#simulators")}
              data-testid="hero-explore-btn"
              className="group inline-flex items-center gap-2 bg-electric text-ink font-display font-bold text-sm uppercase tracking-[0.12em] px-7 py-4 clip-corner hover:bg-electric-hover transition-colors duration-200"
            >
              Explore Simulators
              <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
            </button>
            <button
              onClick={onQuote}
              data-testid="hero-pricing-btn"
              className="inline-flex items-center gap-2 border border-slate-500/60 text-slate-100 font-display font-bold text-sm uppercase tracking-[0.12em] px-7 py-4 clip-corner hover:border-electric hover:text-electric transition-colors duration-200"
            >
              Request Event Pricing
            </button>
          </motion.div>
        </div>
      </motion.div>

      <motion.a
        href="#solutions"
        onClick={(e) => {
          e.preventDefault();
          onNavigate("#solutions");
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-36 lg:bottom-40 right-6 lg:right-10 hidden sm:flex flex-col items-center gap-2 text-slate-500 hover:text-electric transition-colors"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to content"
      >
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] [writing-mode:vertical-lr]">Scroll</span>
        <ChevronDown className="h-4 w-4 animate-bounce" />
      </motion.a>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1, duration: 0.9, ease: EASE }}
        className="relative border-t border-white/10 bg-ink/60 backdrop-blur-md"
        data-testid="value-prop-bar"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4">
          {VALUE_PROPS.map((v, i) => (
            <div
              key={v.title}
              className={`flex items-start gap-3.5 py-6 lg:py-7 pr-4 ${i !== 0 ? "lg:border-l lg:border-white/10 lg:pl-6" : ""}`}
              data-testid={`value-prop-${i}`}
            >
              <v.icon className="h-5 w-5 text-electric shrink-0 mt-0.5" strokeWidth={1.8} />
              <div>
                <p className="font-display font-bold text-sm text-slate-100 uppercase tracking-wide">{v.title}</p>
                <p className="text-xs text-slate-400 mt-1">{v.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
};
